import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Info, Save } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';

interface WorkflowNode {
  id: string;
  type: string;
  label: string;
}

interface WorkflowConnection {
  id: string;
  source: string;
  target: string;
}

interface Workflow {
  nodes: WorkflowNode[];
  connections: WorkflowConnection[];
}

interface ProcessData {
  name?: string;
  description?: string;
  category?: string;
  status?: string;
}

interface SaveWorkflowModalProps {
  workflow: Workflow;
  existingProcess?: ProcessData | null;
  onClose: () => void;
  onSave: () => void;
}

const SaveWorkflowModal = ({ workflow, existingProcess, onClose, onSave }: SaveWorkflowModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: existingProcess?.name || '',
    description: existingProcess?.description || '',
    category: existingProcess?.category || 'custom',
    status: existingProcess?.status || 'active'
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    if (!formData?.name?.trim()) {
      alert(t('Please enter a workflow name'));
      return;
    }

    setIsSaving(true);

    // Simulate save operation
    setTimeout(() => {
      console.log('Saving workflow:', {
        ...formData,
        nodes: workflow?.nodes,
        connections: workflow?.connections,
        lastModified: new Date()?.toISOString()?.split('T')?.[0]
      });
      setIsSaving(false);
      onSave();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {existingProcess ? t('Update Workflow') : t('Save Workflow')}
            </h2>
            <p className="text-sm text-gray-600">{t('Configure workflow details and save')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Workflow Name */}
          <Input
            label={t('Workflow Name')}
            required
            value={formData?.name}
            onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
            placeholder={t('e.g., New Employee Onboarding')}
          />

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              {t('Description')}
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder={t('Describe the purpose and use case for this workflow...')}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
            />
          </div>

          {/* Category */}
          <Select
            label={t('Category')}
            value={formData?.category}
            onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
            options={[
              { value: 'onboarding', label: t('Onboarding Workflows') },
              { value: 'approvals', label: t('Approval Cascades') },
              { value: 'roleTransitions', label: t('Role Transitions') },
              { value: 'custom', label: t('Custom Processes') }
            ]}
          />

          {/* Status */}
          <Select
            label={t('Status')}
            value={formData?.status}
            onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
            options={[
              { value: 'active', label: t('Active') },
              { value: 'draft', label: t('Draft') },
              { value: 'archived', label: t('Archived') }
            ]}
          />

          {/* Workflow Summary */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">{t('Workflow Summary')}</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">{t('Total Nodes')}:</span>
                <span className="ml-2 font-medium text-gray-900">{workflow?.nodes?.length}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('Connections')}:</span>
                <span className="ml-2 font-medium text-gray-900">{workflow?.connections?.length}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('Start Nodes')}:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {workflow?.nodes?.filter(n => n?.type === 'start')?.length}
                </span>
              </div>
              <div>
                <span className="text-gray-600">{t('End Nodes')}:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {workflow?.nodes?.filter(n => n?.type === 'end')?.length}
                </span>
              </div>
            </div>
          </div>

          {existingProcess && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">{t('Version Control')}</p>
                  <p>{t('Saving will create a new version of this workflow. Previous versions will be preserved.')}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('Cancel')}
          </Button>
          <Button
            variant="default"
            iconName="Save"
            onClick={handleSave}
            loading={isSaving}
            disabled={!formData?.name?.trim()}
          >
            {existingProcess ? t('Update Workflow') : t('Save Workflow')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveWorkflowModal;
